import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  estimatedInteriorCost, estimatedExteriorCost, estimatedSystemsCost,
  estimatedRenovationCost, estimatedSoftCosts, formatCurrency,
} from "@/lib/calculations";
import { useCurrency } from "@/lib/CurrencyContext";

const COLORS = {
  Purchase:   "#57534E",  /* stone-600 – dominant slice */
  Interior:   "#A8A29E",  /* stone-400 */
  Exterior:   "#78716C",  /* stone-500 */
  Systems:    "#D97706",  /* amber-600 */
  "Soft Costs": "#059669", /* emerald-600 */
};

export default function CostBreakdownChart({ project }) {
  const currency = useCurrency();
  const purchase = Number(project?.purchase_price) || 0;
  let interior = estimatedInteriorCost(project);
  let exterior = estimatedExteriorCost(project);
  let systems  = estimatedSystemsCost(project);
  // If nothing modelled, use the heuristic reno amount as a single "Renovation" slice
  const modelled = interior + exterior + systems;
  if (modelled === 0) {
    interior = estimatedRenovationCost(project);
    exterior = 0;
    systems  = 0;
  }
  const soft = estimatedSoftCosts(project);

  const data = [
    { name: "Purchase",   value: purchase },
    { name: "Interior",   value: interior },
    { name: "Exterior",   value: exterior },
    { name: "Systems",    value: systems  },
    { name: "Soft Costs", value: soft     },
  ].filter((d) => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);

  if (total === 0) {
    return (
      <div className="fc-card p-6" data-testid="cost-breakdown-empty">
        <div className="fc-label">Cost Breakdown</div>
        <div className="text-xs text-[#A8A29E] mt-6 text-center py-12">
          Enter a purchase price or model a room to see the breakdown.
        </div>
      </div>
    );
  }

  return (
    <div className="fc-card p-6" data-testid="cost-breakdown-chart">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="fc-label">Cost Breakdown</div>
          <div className="fc-heading text-[#1C1C1C] text-lg mt-1">{formatCurrency(total, currency)}</div>
        </div>
        <div className="text-[11px] text-[#A8A29E]">Total project cost</div>
      </div>

      <div className="flex items-center gap-6 flex-wrap">
        <div className="w-[180px] h-[180px] shrink-0">
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={54}
                outerRadius={85}
                paddingAngle={2}
                stroke="none"
                isAnimationActive={false}
              >
                {data.map((d) => (
                  <Cell key={d.name} fill={COLORS[d.name] || "#333"} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(0,0,0,0.10)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#1C1C1C",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                labelStyle={{ color: "#78716C" }}
                formatter={(v) => formatCurrency(v, currency)}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 min-w-[180px] space-y-2">
          {data.map((d) => {
            const pct = (d.value / total) * 100;
            return (
              <div key={d.name} className="flex items-center justify-between gap-3 text-sm" data-testid={`breakdown-${d.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: COLORS[d.name] }} />
                  <span className="text-[#78716C] truncate">{d.name}</span>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <span className="text-[#A8A29E] text-xs fc-mono w-[44px]">{pct.toFixed(1)}%</span>
                  <span className="text-[#1C1C1C] fc-mono">{formatCurrency(d.value, currency)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
